import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import logo from '../assets/budgit_logo.png';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { logout } from '../services/axiosInstance';
import { getBankAccounts } from '../services/BankAccountService';
import axiosInstance from '../services/axiosInstance';
import '../css/Navigation.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationPreferences } from '../contexts/NavigationPreferencesContext';
import { getTenant } from '../utils/tenantUtils';

function Navigation({ balance, setBalance, handleShow }) {
  const { isAuthenticated } = useAuth();
  const { desktopHomePath } = useNavigationPreferences();
  const navigate = useNavigate();
  const tenant = getTenant(); // Get current tenant
  const [showCollapseMenu, setShowCollapseMenu] = useState(false);
  const [userLogo, setUserLogo] = useState(logo)
  const [logoUrl, setLogoUrl] = useState('/static/logos/budgit_logo.png');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [accountsWithBills, setAccountsWithBills] = useState([]);
  const [trackNickname, setTrackNickname] = useState('Bills');
  const [showBillsDropdown, setShowBillsDropdown] = useState(false);

  // Helper function to create tenant-aware paths
  const getTenantPath = (path) => {
    if (tenant) {
      return `/$/${tenant}${path}`;
    }
    return path;
  };

  useEffect(() => {
    const fetchUserLogo = async () => {
      try {
        const response = await axiosInstance.get('/tenant-logo-settings/');
        const logoPath = `/static/logos/${response.data.logo_filename}`;
        setLogoUrl(logoPath);
      } catch (error) {
        console.error('Error fetching user logo:', error.name);
      }
    };

    if (isAuthenticated) {
      fetchUserLogo();
    }

    const handleLogoUpdate = () => fetchUserLogo();
    window.addEventListener('logoUpdated', handleLogoUpdate);

    return () => window.removeEventListener('logoUpdated', handleLogoUpdate);
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        if (accounts && accounts.length > 0) {
          setBankAccounts(accounts);
          
          const trackedAccount = accounts.find(account => account.include_in_tracking);
          if (trackedAccount) {
            setTrackNickname('Bills');
          }
          
          setAccountsWithBills(accounts);
        } else {
          console.warn('No bank accounts returned from API');
        }
      } catch (error) {
        console.error('Error fetching bank accounts:', error.name);
        
        if (error.response && error.response.status === 401) {
          setTimeout(() => {
            if (isAuthenticated) {
              fetchBankAccounts();
            }
          }, 2000);
        }
      }
    };

    if (isAuthenticated) {
      fetchBankAccounts();
    }
  }, [isAuthenticated]);

  const handleShowCollapseMenu = () => {
    setShowCollapseMenu(true);
  };

  const handleCloseCollapseMenu = () => {
    setShowCollapseMenu(false);
  };

  const handleBillsClick = () => {
    setShowBillsDropdown(!showBillsDropdown);
  };

  const handleBankAccountClick = (accountId) => {
    navigate(getTenantPath(`/track/${accountId}`));
    setShowBillsDropdown(false);
    setShowCollapseMenu(false);
  };

  const handleAllBillsClick = () => {
    navigate(getTenantPath('/track'));
    setShowBillsDropdown(false);
    setShowCollapseMenu(false);
  };

  const handleLogout = async () => {
    try {
      const success = await logout();
      if (success) {
        const loginPath = tenant ? `/$/${tenant}/login` : '/login';
        navigate(loginPath, { replace: true });
      } else {
        console.error('Navigation: Logout failed but no error thrown');
      }
    } catch (error) {
      console.error('Navigation: Error during logout:', error.name);
    }
  };

  return (
    <Navbar expand="lg" className="navbar fixed-top desktop-navigation" expanded={showCollapseMenu}>
      <Container className="d-flex justify-content-between align-items-center">
        {isAuthenticated && (
          <Navbar.Brand className="me-0" as={Link} to={desktopHomePath} aria-label="Go to home" title="Home">
            <img
              alt="budgit"
              src={`${window.location.protocol}//${window.location.host}${logoUrl}`}
              className="logo d-inline-block align-top"
            />
          </Navbar.Brand>
        )}

        {isAuthenticated && (
          <div className="navbar-toggle-wrapper d-lg-none">
            <Navbar.Toggle 
              aria-controls="basic-navbar-nav"
              onClick={() => setShowCollapseMenu(!showCollapseMenu)}
            >
              <FontAwesomeIcon icon={faBars} />
            </Navbar.Toggle>
          </div>
        )}

        {isAuthenticated && (
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              {/* Mobile navigation - visible only on smaller screens */}
              <div className="d-lg-none">
                {/* Mobile Bills section */}
                <div className="mobile-nav-section">
                  <div 
                    className="mobile-nav-header"
                    onClick={() => setShowBillsDropdown(!showBillsDropdown)}
                  >
                    <span>{trackNickname}</span>
                    <span className={`dropdown-arrow ${showBillsDropdown ? 'open' : ''}`}>▼</span>
                  </div>
                  
                  {showBillsDropdown && (
                    <div className="mobile-dropdown">
                      <div 
                        className="mobile-dropdown-item"
                        onClick={handleAllBillsClick}
                      >
                        All Bills
                      </div>
                      {accountsWithBills && accountsWithBills.length > 0 && 
                        accountsWithBills.map(account => (
                          <div 
                            key={account.id}
                            className="mobile-dropdown-item"
                            onClick={() => handleBankAccountClick(account.id)}
                          >
                            {account.nickname || account.name}
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>

                <Nav.Link as={NavLink} to={getTenantPath("/transfers")} className="nav-link" onClick={() => setShowCollapseMenu(false)}>
                  Transfers
                </Nav.Link>
                <Nav.Link as={Link} to={getTenantPath("/requirements")} onClick={() => setShowCollapseMenu(false)}>
                  Requirements
                </Nav.Link>
                <Nav.Link as={NavLink} to={getTenantPath("/settings")} className="nav-link" onClick={() => setShowCollapseMenu(false)}>
                  Settings
                </Nav.Link>
                <Nav.Link onClick={() => { handleLogout(); setShowCollapseMenu(false); }} className="nav-link">
                  Logout
                </Nav.Link>
              </div>

              {/* Desktop navigation */}
              <div className="d-none d-lg-flex align-items-center">
                {/* Desktop Bills Dropdown */}
                <NavDropdown 
                  title="Bills" 
                  id="bills-dropdown" 
                  className="nav-dropdown"
                >
                  <NavDropdown.Item 
                    as={NavLink} 
                    to={getTenantPath("/track")}
                    className="dropdown-item"
                    activeClassName="active"
                    end
                  >
                    All Bills
                  </NavDropdown.Item>
                  
                  {accountsWithBills && accountsWithBills.length > 0 && (
                    <>
                      <NavDropdown.Divider />
                      {accountsWithBills.map(account => (
                        <NavDropdown.Item 
                          key={account.id} 
                          as={NavLink} 
                          to={getTenantPath(`/track/${account.id}`)}
                          className="dropdown-item"
                          activeClassName="active"
                        >
                          {account.nickname || account.name}
                        </NavDropdown.Item>
                      ))}
                    </>
                  )}
                </NavDropdown>

                <Nav.Link as={NavLink} to={getTenantPath("/transfers")} className="nav-link" onClick={() => setShowCollapseMenu(false)}>
                  Transfers
                </Nav.Link>
                <Nav.Link as={Link} to={getTenantPath("/requirements")} onClick={() => setShowCollapseMenu(false)}>
                  Requirements
                </Nav.Link>
                <Nav.Link as={NavLink} to={getTenantPath("/settings")} className="nav-link" onClick={() => setShowCollapseMenu(false)}>
                  Settings
                </Nav.Link>
                <Nav.Link onClick={handleLogout} className="nav-link">
                  Logout
                </Nav.Link>
              </div>
            </Nav>
          </Navbar.Collapse>
        )}
      </Container>
    </Navbar>
  );
}

export default Navigation;

